import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsApi } from '../../network/apis/products';
import { vendorsApi } from '../../network/apis/vendors';
import Button from '../../components/elements/button';
import { useCart } from '../../context/CartContext';
import { Back, Store } from '../../assets/icons';
import '../../assets/css/store/product_detail.css';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    
    const [product, setProduct] = useState(null);
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [addingToCart, setAddingToCart] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('description');

    const fetchProduct = useCallback(async () => {
        try {
            setLoading(true);
            const response = await productsApi.getProductById(id);
            const productData = response.data?.data || response.data || response;
            setProduct(productData);
            
            // Fetch vendor details
            if (productData.vendor_id || productData.vendor?.id) {
                const vendorId = productData.vendor_id || productData.vendor?.id;
                try {
                    const vendorResponse = await vendorsApi.getVendorById(vendorId);
                    setVendor(vendorResponse.data?.data || vendorResponse.data || vendorResponse);
                } catch (vendorErr) {
                    console.error('Failed to fetch vendor:', vendorErr);
                }
            } else if (productData.vendor) {
                setVendor(productData.vendor);
            }
            
            // Fetch reviews
            try {
                const reviewsResponse = await productsApi.getProductReviews(id);
                setReviews(reviewsResponse.data?.results || reviewsResponse.data || []);
            } catch (reviewErr) {
                console.error('Failed to fetch reviews:', reviewErr);
            }
            
            // Fetch related products
            if (productData.category?.id || productData.category_id) {
                try {
                    const categoryId = productData.category?.id || productData.category_id;
                    const relatedResponse = await productsApi.getProducts({ 
                        category: categoryId, 
                        limit: 4,
                        exclude: id
                    });
                    const related = relatedResponse.data?.results || relatedResponse.data || [];
                    setRelatedProducts(related.filter(p => p.id !== parseInt(id)).slice(0, 4));
                } catch (relatedErr) {
                    console.error('Failed to fetch related products:', relatedErr);
                }
            }
        } catch (err) {
            console.error('Failed to fetch product:', err);
            setError('Product not found or an error occurred.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchProduct();
    }, [fetchProduct]);

    const handleAddToCart = async () => {
        if (!product) return;
        
        setAddingToCart(true);
        try {
            await addToCart({
                id: product.id,
                name: product.name,
                price: product.discount_price || product.price,
                originalPrice: product.price,
                image: product.primary_image || product.images?.[0]?.image,
                quantity: quantity,
                vendor: vendor?.id || product.vendor,
                vendorId: vendor?.id || product.vendor?.id,
                category: product.category || product.category_id
            });
        } catch (err) {
            console.error('Failed to add to cart:', err);
        } finally {
            setAddingToCart(false);
        }
    };

    const formatPrice = (price) => {
        return new window.Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 0
        }).format(price || 0);
    };

    const getStockStatusClass = (status) => {
        switch (status) {
            case 'IN_STOCK': return 'in-stock';
            case 'LOW_STOCK': return 'low-stock';
            case 'OUT_OF_STOCK': return 'out-of-stock';
            default: return '';
        }
    };

    const getStockStatusLabel = (status) => {
        switch (status) {
            case 'IN_STOCK': return 'In Stock';
            case 'LOW_STOCK': return 'Low Stock';
            case 'OUT_OF_STOCK': return 'Out of Stock';
            default: return status || 'Unknown';
        }
    };

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating || 0);
        const hasHalf = (rating || 0) % 1 >= 0.5;
        
        for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
                stars.push(<span key={i} className="star filled">★</span>);
            } else if (i === fullStars && hasHalf) {
                stars.push(<span key={i} className="star half">★</span>);
            } else {
                stars.push(<span key={i} className="star empty">☆</span>);
            }
        }
        return stars;
    };

    if (loading) {
        return (
            <div className="product-detail-page">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading product...</p>
                </div>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="product-detail-page">
                <div className="error-state">
                    <div className="error-icon">📦</div>
                    <h2>Product Not Found</h2>
                    <p>{error || 'The product you are looking for does not exist.'}</p>
                    <Button 
                        text="Browse Products" 
                        variant="primary" 
                        onClick={() => navigate('/store')}
                    />
                </div>
            </div>
        );
    }

    const images = product.images || [];
    const primaryImage = product.primary_image || images[0]?.image;
    const allImages = primaryImage ? [{ image: primaryImage }, ...images.filter(img => img.image !== primaryImage)] : images;

    return (
        <div className="product-detail-page">
            <div className="breadcrumb">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <img src={Back} alt="Back" />
                    Back
                </button>
                <span className="separator">/</span>
                <Link to="/store">Store</Link>
                <span className="separator">/</span>
                {product.category && (
                    <>
                        <Link to={`/store?category=${product.category.id}`}>{product.category.name}</Link>
                        <span className="separator">/</span>
                    </>
                )}
                <span className="current">{product.name}</span>
            </div>

            <div className="product-detail-container">
                {/* Product Gallery */}
                <div className="product-gallery">
                    <div className="main-image">
                        {allImages.length > 0 ? (
                            <img 
                                src={allImages[selectedImage]?.image || allImages[selectedImage]?.url} 
                                alt={product.name} 
                            />
                        ) : (
                            <div className="no-image">
                                <span>📷</span>
                                <p>No image available</p>
                            </div>
                        )}
                        
                        {product.discount_price && product.discount_price < product.price && (
                            <div className="discount-badge">
                                -{Math.round((1 - product.discount_price / product.price) * 100)}%
                            </div>
                        )}
                    </div>
                    
                    {allImages.length > 1 && (
                        <div className="thumbnail-list">
                            {allImages.map((img, index) => (
                                <button
                                    key={index}
                                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                                    onClick={() => setSelectedImage(index)}
                                >
                                    <img src={img.image || img.url} alt={`${product.name} ${index + 1}`} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Product Info */}
                <div className="product-info">
                    <div className="product-header">
                        <span className={`stock-badge ${getStockStatusClass(product.stock_status)}`}>
                            {getStockStatusLabel(product.stock_status)}
                        </span>
                        {product.is_featured && <span className="featured-badge">Featured</span>}
                    </div>

                    <h1 className="product-title">{product.name}</h1>
                    
                    {product.sku && <p className="product-sku">SKU: {product.sku}</p>}

                    <div className="product-rating">
                        <div className="stars">{renderStars(product.average_rating)}</div>
                        <span className="rating-count">
                            ({product.review_count || reviews.length || 0} reviews)
                        </span>
                    </div>

                    <div className="product-price">
                        {product.discount_price && product.discount_price < product.price ? (
                            <>
                                <span className="current-price">{formatPrice(product.discount_price)}</span>
                                <span className="original-price">{formatPrice(product.price)}</span>
                            </>
                        ) : (
                            <span className="current-price">{formatPrice(product.price)}</span>
                        )}
                    </div>

                    {product.stock_status !== 'OUT_OF_STOCK' && (
                        <div className="quantity-selector">
                            <label>Quantity:</label>
                            <div className="quantity-controls">
                                <button 
                                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                    disabled={quantity <= 1}
                                >
                                    -
                                </button>
                                <input 
                                    type="number" 
                                    value={quantity}
                                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                    min="1"
                                    max={product.stock_quantity || 99}
                                />
                                <button 
                                    onClick={() => setQuantity(q => Math.min(product.stock_quantity || 99, q + 1))}
                                    disabled={quantity >= (product.stock_quantity || 99)}
                                >
                                    +
                                </button>
                            </div>
                            {product.stock_quantity && (
                                <span className="stock-count">{product.stock_quantity} available</span>
                            )}
                        </div>
                    )}

                    <div className="product-actions">
                        <Button
                            text={addingToCart ? 'Adding...' : 'Add to Cart'}
                            variant="primary"
                            onClick={handleAddToCart}
                            loading={addingToCart}
                            disabled={addingToCart || product.stock_status === 'OUT_OF_STOCK'}
                        />
                        <Button
                            text="Buy Now"
                            variant="outline"
                            onClick={() => {
                                handleAddToCart();
                                navigate('/cart');
                            }}
                            disabled={product.stock_status === 'OUT_OF_STOCK'}
                        />
                    </div>

                    {/* Vendor Info */}
                    {vendor && (
                        <div className="vendor-card">
                            <div className="vendor-header">
                                <div className="vendor-logo">
                                    {vendor.store_logo ? (
                                        <img src={vendor.store_logo} alt={vendor.store_name} />
                                    ) : (
                                        <img src={Store} alt="Store" />
                                    )}
                                </div>
                                <div className="vendor-details">
                                    <h4>{vendor.store_name}</h4>
                                    <p className="vendor-location">{vendor.city}, {vendor.state}</p>
                                </div>
                            </div>
                            <Link to={`/store?vendor=${vendor.id}`} className="view-store-btn">
                                View Store
                            </Link>
                        </div>
                    )}

                    {/* Product Meta */}
                    <div className="product-meta">
                        {product.category && (
                            <div className="meta-item">
                                <span className="meta-label">Category:</span>
                                <Link to={`/store?category=${product.category.id}`} className="meta-value">
                                    {product.category.name}
                                </Link>
                            </div>
                        )}
                        {product.manufacturer && (
                            <div className="meta-item">
                                <span className="meta-label">Brand:</span>
                                <span className="meta-value">{product.manufacturer}</span>
                            </div>
                        )}
                        {product.weight && (
                            <div className="meta-item">
                                <span className="meta-label">Weight:</span>
                                <span className="meta-value">{product.weight} kg</span>
                            </div>
                        )}
                        {product.dimensions && (
                            <div className="meta-item">
                                <span className="meta-label">Dimensions:</span>
                                <span className="meta-value">{product.dimensions}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs Section */}
            <div className="product-tabs">
                <div className="tab-headers">
                    <button 
                        className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                        onClick={() => setActiveTab('description')}
                    >
                        Description
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reviews')}
                    >
                        Reviews ({reviews.length})
                    </button>
                </div>

                <div className="tab-content">
                    {activeTab === 'description' && (
                        <div className="description-tab">
                            {product.description ? (
                                <p>{product.description}</p>
                            ) : (
                                <p className="no-content">No description available for this product.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'reviews' && (
                        <div className="reviews-tab">
                            {reviews.length > 0 ? (
                                <div className="reviews-list">
                                    {reviews.map((review, index) => (
                                        <div key={review.id || index} className="review-card">
                                            <div className="review-header">
                                                <div className="reviewer-info">
                                                    <div className="reviewer-avatar">
                                                        {review.user?.first_name?.[0] || 'U'}
                                                    </div>
                                                    <div>
                                                        <h4>{review.user?.first_name || 'Anonymous'}</h4>
                                                        <span className="review-date">
                                                            {new Date(review.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="review-rating">
                                                    {renderStars(review.rating)}
                                                </div>
                                            </div>
                                            <p className="review-text">{review.comment}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-reviews">
                                    <p>No reviews yet. Be the first to review this product!</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Related Products */}
            {relatedProducts.length > 0 && (
                <div className="related-products">
                    <h2>Related Products</h2>
                    <div className="products-grid">
                        {relatedProducts.map(relatedProduct => (
                            <Link 
                                key={relatedProduct.id} 
                                to={`/marketplace/product/${relatedProduct.id}`}
                                className="product-card"
                            >
                                <div className="product-image">
                                    {relatedProduct.primary_image || relatedProduct.images?.[0]?.image ? (
                                        <img 
                                            src={relatedProduct.primary_image || relatedProduct.images[0].image} 
                                            alt={relatedProduct.name} 
                                        />
                                    ) : (
                                        <div className="no-image">📷</div>
                                    )}
                                </div>
                                <div className="product-details">
                                    <h4>{relatedProduct.name}</h4>
                                    <p className="price">{formatPrice(relatedProduct.discount_price || relatedProduct.price)}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetail;
